import React, { useEffect, useState } from 'react'

import Row from '@amzn/meridian/row'
import Column from '@amzn/meridian/column'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import userToken from '@amzn/meridian-tokens/base/icon/user'

import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { authService } from '@services/auth/AuthService'
import { useLogout } from '@hooks/useLogout'

interface IUserProfile {
    name: string
    email: string
}

/**
 * Inline profile area for the navbar in Cloudscape (Portal) mode.
 *
 * Shows the signed-in user's avatar, display name, and email alongside a
 * "Sign Out" button. Identity is resolved from the OIDC ID token claims via
 * AuthService.getUserProfile(); sign-out goes through the shared useLogout hook
 * (the same unified logout used by the profile sheet and sidebar footer).
 *
 * @returns {React.ReactElement}
 */
const NavBarProfile = (): React.ReactElement => {
    const [profile, setProfile] = useState<IUserProfile>({ name: '', email: '' })
    const [bundle] = useBundle(BUNDLE_NAME)
    const logout = useLogout()

    useEffect(() => {
        let isMounted = true
        authService.getUserProfile()
            .then((resolved) => {
                if (isMounted) setProfile(resolved)
            })
            .catch((err) => {
                console.warn('[NavBarProfile] Could not resolve user profile:', err)
            })
        return () => {
            isMounted = false
        }
    }, [])

    return (
        <Row alignmentVertical="center" spacing="small" data-testid="navbar-profile">
            <div className="navbar-profile-avatar" aria-hidden="true">
                <Icon tokens={userToken}>{t(bundle, 'jsma-web-common-profile')}</Icon>
            </div>
            {(profile.name || profile.email) && (
                <Column spacing="none">
                    {profile.name && (
                        <Text type="b300" data-testid="navbar-profile-name">{profile.name}</Text>
                    )}
                    {profile.email && (
                        <Text type="b200" color="secondary" data-testid="navbar-profile-email">
                            {profile.email}
                        </Text>
                    )}
                </Column>
            )}
            <Button
                type="secondary"
                size="small"
                onClick={logout}
                data-testid="navbar-profile-signout"
            >
                {t(bundle, 'jsma-web-common-signOut')}
            </Button>
        </Row>
    )
}

export default NavBarProfile
